import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  ProgressBar,
  Alert,
  Spinner,
  Tabs,
  Tab,
  ListGroup,
  Form,
  Modal,
} from "react-bootstrap";
import {
  FaStore,
  FaUser,
  FaCalendar,
  FaStar,
  FaChartLine,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaEye,
  FaBell,
} from "react-icons/fa";

const VendorInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendorData, setVendorData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [earningsFilter, setEarningsFilter] = useState("month");
  const [bookingsFilter, setBookingsFilter] = useState("all");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [repeatCustomers, setRepeatCustomers] = useState([]);
  const [showRepeatModal, setShowRepeatModal] = useState(false);
  const [repeatCustomerBookings, setRepeatCustomerBookings] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [monthlyEarnings, setMonthlyEarnings] = useState([]);
  const [weeklyEarnings, setWeeklyEarnings] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [vendorReviews, setVendorReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [adminModeOfPayment, setAdminModeOfPayment] = useState('');
  const [kycData, setKycData] = useState(null);
  const [securityDeposit, setSecurityDeposit] = useState(null);
const [securityDepositLoading, setSecurityDepositLoading] = useState(false);
const [adminPaymentId, setAdminPaymentId] = useState('');
const [adminOrderId, setAdminOrderId] = useState('');
const [markingDeposit, setMarkingDeposit] = useState(false);
const [adminAmount, setAdminAmount] = useState('');
const [depositMessage, setDepositMessage] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [taxRates, setTaxRates] = useState({
    gst: 0,
    serviceTax: 0,
    platformFee: 0,
  });
  useEffect(() => {
    fetchVendorData();
    fetchAnalytics();
    fetchTaxRates();
  }, [id]);

  useEffect(() => {
    if (activeTab === "reviews" && vendorData && vendorReviews.length === 0) {
      fetchVendorReviews();
    }
  }, [activeTab, vendorData]);

  useEffect(() => {
    if (activeTab === "kyc") {
      fetchKycData();
    }

    if (activeTab === "security-deposit") {   // ← ADD THIS
    fetchSecurityDeposit();
  }
  }, [activeTab]);

  const deriveBilling = (plan, providedType) => {
    const type = (providedType || "").toString().toLowerCase();
    if (type && type !== "—") return providedType;
    const p = (plan || "").toString().toLowerCase();
    if (p === "basic") return "monthly";
    if (p === "standard") return "quarterly";
    if (p === "premium") return "yearly";
    if (p === "none") return "30 days";
    return providedType || "—";
  };

  const fetchVendorData = async () => {
    try {
      setLoading(true);
      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            typeof window !== "undefined" ? window.location.origin : "",
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let data = null;
      let lastError = null;
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of candidateBases) {
        try {
          const res = await fetch(`${base}/api/vendors/${id}`, {
            headers,
            method: "GET",
          });
          const contentType = res.headers.get("content-type") || "";
          if (!res.ok) {
            const body = contentType.includes("application/json")
              ? JSON.stringify(await res.json())
              : await res.text();
            throw new Error(
              `Failed to load vendor: ${res.status} ${
                res.statusText
              }. Body: ${body.slice(0, 200)}`,
            );
          }
          if (!contentType.includes("application/json")) {
            throw new Error(`Expected JSON but got ${contentType}`);
          }
          data = await res.json();
          break;
        } catch (e) {
          lastError = e;
          continue;
        }
      }

      if (!data) {
        throw lastError || new Error("Unable to fetch vendor data");
      }

      const v = data.vendor;
      if (!v) throw new Error("No vendor returned");

      const personal = {
        ...(v.vendor?.personal_info || {}),
        ...(v.personal_info || {}),
        name:
          v.vendor?.personal_info?.name ||
          v.personal_info?.name ||
          v.businessName ||
          v.name,
        email:
          v.vendor?.personal_info?.email || v.personal_info?.email || v.email,
        phone:
          v.vendor?.personal_info?.phone ||
          v.personal_info?.phone ||
          v.phoneNumber ||
          v.phone,
        address:
          v.vendor?.personal_info?.address ||
          v.personal_info?.address ||
          v.address,
        about:
          v.vendor?.personal_info?.about ||
          v.personal_info?.about ||
          v.about ||
          v.description,
      };

      const categories = v.vendor?.categories || v.categories || [];
      const flattenedServices = categories.flatMap((cat) =>
        (cat.services || []).map((s) => ({
          id: s.serviceId || `${cat.category_name}-${s.service_name}`,
          name: s.service_name || s.name || "Service",
          description: s.description || "",
          price:
            s.price ?? s.rate ?? s.amount ?? s.cost ?? s.pricing?.price ?? 0,
          duration: s.duration || s.pricing?.duration || "—",
          availability: Array.isArray(s.availability)
            ? s.availability
            : Array.isArray(v.vendor?.availability)
              ? v.vendor.availability
              : [],
          category: cat.category_name || v.businessCategory || "General",
        })),
      );

      const categoryNames = Array.isArray(categories)
        ? categories.map((c) => c.category_name || c.name || "General")
        : [];

      const sub = v.subscription || null;
      let normalizedSubscription = sub
        ? {
            ...sub,
            plan:
              (sub.plan || sub.name || "—").toString().toLowerCase() === "free"
                ? "none"
                : sub.plan || sub.name || "—",
            type: deriveBilling(
              sub.plan || sub.name,
              sub.type || sub.billing || "—",
            ),
            status: sub.status || "active",
            startDate:
              sub.startDate || sub.start_date || sub.started_at || null,
            endDate: sub.endDate || sub.end_date || sub.ends_at || null,
          }
        : null;

      try {
        const nowIso = new Date().toISOString();
        if (!normalizedSubscription) {
          const start = new Date(nowIso);
          const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
          normalizedSubscription = {
            plan: "none",
            type: "—",
            status: "active",
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          };
        } else {
          const planLc = String(
            normalizedSubscription.plan || "",
          ).toLowerCase();
          const typeLc = String(
            normalizedSubscription.type || "",
          ).toLowerCase();
          const startIso = normalizedSubscription.startDate || nowIso;
          const start = new Date(startIso);
          if (!isNaN(start)) {
            const addMonths = (date, months) => {
              const d = new Date(
                Date.UTC(
                  date.getUTCFullYear(),
                  date.getUTCMonth(),
                  date.getUTCDate(),
                  date.getUTCHours(),
                  date.getUTCMinutes(),
                  date.getUTCSeconds(),
                  date.getUTCMilliseconds(),
                ),
              );
              const y = d.getUTCFullYear();
              const m = d.getUTCMonth();
              const day = d.getUTCDate();
              const h = d.getUTCHours();
              const mi = d.getUTCMinutes();
              const s = d.getUTCSeconds();
              const ms = d.getUTCMilliseconds();
              const target = new Date(Date.UTC(y, m + months, 1, h, mi, s, ms));
              const daysInTargetMonth = new Date(
                Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
              ).getUTCDate();
              target.setUTCDate(Math.min(day, daysInTargetMonth));
              return target;
            };
            if (planLc === "none") {
              const minEnd = new Date(
                start.getTime() + 30 * 24 * 60 * 60 * 1000,
              );
              if (
                !normalizedSubscription.endDate ||
                new Date(normalizedSubscription.endDate) < minEnd
              ) {
                normalizedSubscription = {
                  ...normalizedSubscription,
                  startDate: start.toISOString(),
                  endDate: minEnd.toISOString(),
                };
              }
            } else if (typeLc === "quarterly") {
              const minEnd = addMonths(start, 3);
              if (
                !normalizedSubscription.endDate ||
                new Date(normalizedSubscription.endDate) < minEnd
              ) {
                normalizedSubscription = {
                  ...normalizedSubscription,
                  startDate: start.toISOString(),
                  endDate: minEnd.toISOString(),
                };
              }
            } else if (
              typeLc === "yearly" ||
              typeLc === "annual" ||
              typeLc === "annually"
            ) {
              const minEnd = addMonths(start, 12);
              if (
                !normalizedSubscription.endDate ||
                new Date(normalizedSubscription.endDate) < minEnd
              ) {
                normalizedSubscription = {
                  ...normalizedSubscription,
                  startDate: start.toISOString(),
                  endDate: minEnd.toISOString(),
                };
              }
            }
          }
        }
      } catch (_) {}

      const normalizedEmail =
        personal.email ||
        v.email ||
        v.contactInfo?.email ||
        v.contact?.email ||
        v.vendor?.contact?.email ||
        "—";

      const normalizedPhone =
        personal.phone ||
        v.contactInfo?.phone ||
        v.personal_info?.phone ||
        v.businessPhone ||
        v.phoneNumber ||
        v.mobile ||
        v.whatsapp ||
        v.contact ||
        v.contact_number ||
        v.phone ||
        "—";

      const normalizedAddress =
        personal.address ||
        v.address ||
        v.location?.address ||
        v.vendor?.address ||
        v.vendor?.personal_info?.address ||
        "—";

      const normalizedPincode =
        personal.pincode ||
        v.pincode ||
        v.vendor?.personal_info?.pincode ||
        v.vendor?.address?.pincode ||
        v.address?.pincode ||
        "—";

      const normalizedDescription =
        personal.about ||
        v.description ||
        v.businessDescription ||
        v.vendor?.about ||
        v.vendor?.personal_info?.about ||
        v.bio ||
        "—";

      const normalizedVendor = {
        id: v.id,
        businessName: personal.name || v.businessName || v.name || "—",
        businessCategory: v.businessCategory || v.category || "—",
        businessDescription: normalizedDescription,
        contactInfo: {
          email: normalizedEmail,
          phone: normalizedPhone,
          address: normalizedAddress,
          pincode: normalizedPincode,
          societyName:
            personal.societyName ||
            v.societyName ||
            v.vendor?.personal_info?.societyName ||
            "—",
        },
        categories: categoryNames,
        status: v.status || "active",
        isVerified: v.verification?.isVerified || v.verified || false,
        rating: v.reviews?.averageRating || 0,
        totalReviews: v.reviews?.totalReviews || 0,
        totalBookings: v.metrics?.totalBookings || 0,
        totalEarnings: v.metrics?.totalEarnings || 0,
        memberSince: v.createdAt || new Date().toISOString(),
        subscription: normalizedSubscription,
        earnings: analytics?.earnings || {
          week: 0,
          month: 0,
          year: 0,
          allTime: 0,
        },
        reviews: [],
        preferences: {
          notifications: v.preferences?.notifications ?? true,
          emailUpdates: v.preferences?.emailUpdates ?? true,
          smsUpdates: v.preferences?.smsUpdates ?? true,
        },
        referralCode:
          v.referral_info?.referral_code ||
          v.vendor?.referral_info?.referral_code ||
          "—",
        referralPoints:
          v.referral_info?.referral_points ||
          v.vendor?.referral_info?.referral_points ||
          0,
        referralsCount:
          v.referral_info?.referrals_count ||
          v.vendor?.referral_info?.referrals_count ||
          0,
        certificatesUrl:
          v.documents?.certificates_url ||
          v.vendor?.documents?.certificates_url ||
          null,
        availability: v.availability || v.vendor?.availability || [],
      };

      setVendorData(normalizedVendor);
      setServices(flattenedServices);

      try {
        const successfulBase = candidateBases.find((base) => !!base) || "";
        const adminToken =
          localStorage.getItem("adminToken") ||
          sessionStorage.getItem("adminToken");
        const headers = {
          "Content-Type": "application/json",
        };
        if (adminToken) {
          headers["Authorization"] = `Bearer ${adminToken}`;
        }

        const avRes = await fetch(
          `${successfulBase}/api/vendors/${id}/availability`,
          {
            headers,
            method: "GET",
          },
        );
        if (avRes.ok) {
          const avJson = await avRes.json();
          const availability = Array.isArray(avJson.availability)
            ? avJson.availability
            : [];
          setServices((prev) => prev.map((svc) => ({ ...svc, availability })));
        }
      } catch (e) {
        console.warn("Availability fetch failed:", e);
      }

      await fetchVendorBookings(id, candidateBases);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      setError("Failed to load vendor data");
      setLoading(false);
    }
  };

  const fetchVendorReviews = async () => {
    try {
      setReviewsLoading(true);
      console.log(`🔍 Fetching reviews for vendor: ${id}`);

      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let reviewsData = null;
      let lastError = null;

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of candidateBases) {
        try {
          console.log(`🔍 Trying reviews API with base: ${base}`);

          const reviewsResponse = await fetch(
            `${base}/api/reviews/vendor/${id}`,
            {
              headers,
              method: "GET",
            },
          );
          if (reviewsResponse.ok) {
            const data = await reviewsResponse.json();
            reviewsData = data;
            console.log(`✅ Reviews fetched successfully from: ${base}`);
            break;
          } else {
            console.log(
              `❌ Reviews API failed with status: ${reviewsResponse.status}`,
            );
          }
        } catch (error) {
          console.log(`❌ Reviews API error with base ${base}:`, error.message);
          lastError = error;
        }
      }

      if (reviewsData && reviewsData.reviews) {
        console.log(`📊 Reviews data received:`, reviewsData);
        setVendorReviews(reviewsData.reviews);
      } else {
        console.warn("⚠️ Could not fetch reviews data");
        setVendorReviews([]);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setVendorReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchKycData = async () => {
    try {
      setKycLoading(true);
      console.log(`🔍 Fetching KYC data for vendor: ${id}`);

      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let kycData = null;
      let lastError = null;

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of candidateBases) {
        try {
          console.log(`🔍 Trying KYC API with base: ${base}`);

          const kycResponse = await fetch(`${base}/api/admins/vendors/${id}`, {
            headers,
            method: "GET",
          });
          console.log(`📊 KYC API response status: ${kycResponse.status}`);

          if (kycResponse.ok) {
            const data = await kycResponse.json();
            console.log(`📊 Full API response:`, data);

            if (data && data.vendor) {
              kycData = data.vendor;
              console.log(`✅ KYC data fetched successfully from: ${base}`);
              console.log(`📊 Vendor data structure:`, kycData);
              break;
            } else {
              console.log(`❌ Invalid response structure from ${base}`);
            }
          } else {
            console.log(`❌ KYC API failed with status: ${kycResponse.status}`);
            const errorText = await kycResponse.text();
            console.log(`❌ KYC API error response:`, errorText);
          }
        } catch (error) {
          console.log(`❌ KYC API error with base ${base}:`, error.message);
          lastError = error;
        }
      }

      // The kycData is the vendor object which contains kycInfo directly
      if (kycData && kycData.kycInfo) {
        console.log(`📊 KYC data received:`, kycData.kycInfo);
        setKycData(kycData.kycInfo);
      } else {
        console.warn("⚠️ Could not fetch KYC data or KYC info is missing");
        console.log(`📊 Vendor data without KYC info:`, kycData);
        // Even if kycInfo is missing, we should still set the data to trigger a re-render
        if (kycData) {
          setKycData(kycData.kycInfo || null);
        } else {
          setKycData(null);
        }
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
      setKycData(null);
    } finally {
      setKycLoading(false);
    }
  };

  const fetchSecurityDeposit = async () => {
  try {
    setSecurityDepositLoading(true);
    const candidateBases = Array.from(new Set([
      process.env.REACT_APP_API_BASE_URL,
      "https://api.quvikme.com",
    ].filter(Boolean)));

    const adminToken = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
    const headers = { "Content-Type": "application/json" };
    if (adminToken) headers["Authorization"] = `Bearer ${adminToken}`;

    for (const base of candidateBases) {
      try {
        const res = await fetch(`${base}/api/vendors/${id}/security-deposit/status`, { headers });
        if (res.ok) {
          const data = await res.json();
          setSecurityDeposit(data.securityDeposit);
          break;
        }
      } catch (e) { continue; }
    }
  } catch (err) {
    console.error("Error fetching security deposit:", err);
  } finally {
    setSecurityDepositLoading(false);
  }
};

const handleAdminMarkDeposit = async () => {
  if (!adminAmount || Number(adminAmount) <= 0) {
    setDepositMessage({ type: 'danger', text: 'Please enter a valid amount.' });
    return;
  }
  if (!window.confirm(
    `Record payment of ₹${adminAmount} via ${adminModeOfPayment}? This cannot be undone.`
  )) return;

  try {
    setMarkingDeposit(true);
    setDepositMessage(null);
    const candidateBases = Array.from(new Set([
      process.env.REACT_APP_API_BASE_URL,
      "https://api.quvikme.com",
    ].filter(Boolean)));

    const adminToken = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
    const headers = { "Content-Type": "application/json" };
    if (adminToken) headers["Authorization"] = `Bearer ${adminToken}`;

    for (const base of candidateBases) {
      try {
        const res = await fetch(
          `${base}/api/vendors/${id}/security-deposit/admin-mark`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              amount: Number(adminAmount),          // ← NEW
              modeOfPayment: adminModeOfPayment,
              paymentId: adminPaymentId.trim() || null,
              orderId: adminOrderId.trim() || null,
            }),
          }
        );
        if (res.ok) {
          const data = await res.json();
          setSecurityDeposit(data.securityDeposit);
          setAdminPaymentId('');
          setAdminOrderId('');
          setAdminModeOfPayment('');
          setAdminAmount('');                       // ← NEW
          const remaining = data.securityDeposit?.remainingAmount ?? 0;
          setDepositMessage({
            type: 'success',
            text: data.securityDeposit?.paid
              ? '✅ Security deposit fully paid!'
              : `✅ Payment of ₹${adminAmount} recorded. ₹${remaining} still remaining.`,
          });
          break;
        } else {
          const err = await res.json();
          setDepositMessage({ type: 'danger', text: err.error || 'Failed to record payment.' });
        }
      } catch (e) {
        setDepositMessage({ type: 'danger', text: e.message });
      }
    }
  } finally {
    setMarkingDeposit(false);
  }
};

  const fetchTaxRates = async () => {
    try {
      console.log(`🔍 Fetching tax rates`);

      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let taxData = null;
      let lastError = null;

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of candidateBases) {
        try {
          console.log(`🔍 Trying tax rates API with base: ${base}`);

          const taxResponse = await fetch(
            `${base}/api/admin/config/tax-rates`,
            {
              headers,
              method: "GET",
            },
          );

          if (taxResponse.ok) {
            const data = await taxResponse.json();
            taxData = data.taxRates || {
              gst: 0,
              serviceTax: 0,
              platformFee: 0,
            };
            console.log(`✅ Tax rates fetched successfully from: ${base}`);
            break;
          } else {
            console.log(
              `❌ Tax rates API failed with status: ${taxResponse.status}`,
            );
          }
        } catch (error) {
          console.log(
            `❌ Tax rates API error with base ${base}:`,
            error.message,
          );
          lastError = error;
        }
      }

      if (taxData) {
        console.log(`📊 Tax rates data received:`, taxData);
        setTaxRates(taxData);
      } else {
        console.warn("⚠️ Could not fetch tax rates, using defaults");
        setTaxRates({ gst: 0, serviceTax: 0, platformFee: 0 });
      }
    } catch (error) {
      console.error("Error fetching tax rates:", error);
      setTaxRates({ gst: 0, serviceTax: 0, platformFee: 0 });
    }
  };

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      console.log(`📊 Fetching analytics for vendor: ${id}`);

      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let analyticsData = null;
      let monthlyEarningsData = [];
      let weeklyEarningsData = [];
      let lastError = null;

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of candidateBases) {
        try {
          console.log(`🔍 Trying analytics API with base: ${base}`);

          const statsResponse = await fetch(
            `${base}/api/vendors/${id}/statistics`,
            {
              headers,
              method: "GET",
            },
          );
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            analyticsData = statsData;
            console.log(`✅ Analytics fetched successfully from: ${base}`);

            const monthlyResponse = await fetch(
              `${base}/api/vendors/${id}/monthly-earnings`,
              { headers },
            );
            if (monthlyResponse.ok) {
              const monthlyData = await monthlyResponse.json();
              monthlyEarningsData = monthlyData.monthlyEarnings || [];
              console.log(
                `✅ Monthly earnings fetched successfully from: ${base}`,
              );
            }

            const weeklyResponse = await fetch(
              `${base}/api/vendors/${id}/weekly-earnings`,
              { headers },
            );
            if (weeklyResponse.ok) {
              const weeklyData = await weeklyResponse.json();
              weeklyEarningsData = weeklyData.weeklyEarnings || [];
              console.log(
                `✅ Weekly earnings fetched successfully from: ${base}`,
              );
            }
            break;
          } else {
            console.log(
              `❌ Analytics API failed with status: ${statsResponse.status}`,
            );
          }
        } catch (error) {
          console.log(
            `❌ Analytics API error with base ${base}:`,
            error.message,
          );
          lastError = error;
        }
      }

      if (analyticsData) {
        console.log(`📊 Analytics data received:`, analyticsData);
        setAnalytics(analyticsData);
        setMonthlyEarnings(monthlyEarningsData);
        setWeeklyEarnings(weeklyEarningsData);
        console.log(`📊 Analytics data loaded:`, analyticsData);
      } else {
        console.warn("⚠️ Could not fetch analytics data, using fallback");
        setAnalytics({
          totalBookings: 0,
          totalEarnings: 0,
          averageRating: 0,
          completedBookings: 0,
          pendingBookings: 0,
          cancelledBookings: 0,
          thisMonthEarnings: 0,
          thisMonthBookings: 0,
          lastMonthEarnings: 0,
          lastMonthBookings: 0,
          thisWeekEarnings: 0,
          thisWeekBookings: 0,
          thisYearEarnings: 0,
          thisYearBookings: 0,
          responseRate: 0,
          averageResponseTime: 0,
          earnings: { week: 0, month: 0, year: 0, allTime: 0 },
        });
        setMonthlyEarnings([]);
        setWeeklyEarnings([]);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setError("Failed to load analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchVendorBookings = async (vendorId, candidateBases) => {
    try {
      setBookingsLoading(true);
      console.log(`📋 Fetching bookings for vendor: ${vendorId}`);

      // Use dynamic base URL approach like other admin components
      const bases =
        candidateBases ||
        Array.from(
          new Set(
            [
              process.env.REACT_APP_API_BASE_URL,
              "https://api.quvikme.com",
            ].filter(Boolean),
          ),
        );

      let bookingsData = null;
      let lastError = null;

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of bases) {
        try {
          const res = await fetch(
            `${base}/api/vendors/${vendorId}/bookings?limit=20000`,
            {
              headers,
              method: "GET",
            },
          );
          const contentType = res.headers.get("content-type") || "";

          if (!res.ok) {
            const body = contentType.includes("application/json")
              ? JSON.stringify(await res.json())
              : await res.text();
            throw new Error(
              `Failed to load bookings: ${res.status} ${
                res.statusText
              }. Body: ${body.slice(0, 200)}`,
            );
          }

          if (!contentType.includes("application/json")) {
            throw new Error(`Expected JSON but got ${contentType}`);
          }

          bookingsData = await res.json();
          break;
        } catch (e) {
          lastError = e;
          continue;
        }
      }

      if (!bookingsData) {
        throw lastError || new Error("Unable to fetch vendor bookings");
      }

      const normalizedBookings = (bookingsData.bookings || []).map(
        (booking) => ({
          id: booking.id,
          customerName: booking.customerName || "Unknown Customer",
          serviceName: booking.serviceName || "Service",
          dateTime: booking.dateTime || booking.createdAt,
          status: booking.status || "pending",
          price:
            typeof booking.price === "number"
              ? booking.price
              : parseFloat(booking.price || 0),
          rating: booking.rating || null,
          customerPhone: booking.customerPhone || "",
          customerAddress: booking.address || booking.customerAddress || "",
          customerDescription: booking.description || "",
          customerImage: booking.customerImage || "",
          vendorName: booking.vendorName || "",
          vendorImage: booking.vendorImage || "",
          vendorAddress: booking.vendorAddress || "",
          vendorPhone: booking.vendorPhone || "",
          vendorRating: booking.vendorRating || 0,
          createdAt: booking.createdAt,
          updatedAt: booking.updatedAt,
          userId: booking.userId || null,
        }),
      );

      console.log(
        `✅ Retrieved ${normalizedBookings.length} bookings for vendor ${vendorId}`,
      );
      setBookings(normalizedBookings);

      const repeatMap = {};
      const customerToBookings = {};
      for (const b of normalizedBookings) {
        const key = b.userId || b.customerPhone || `${b.customerName}`;
        if (!key) continue;
        if (!repeatMap[key]) {
          repeatMap[key] = {
            key,
            userId: b.userId || null,
            name: b.customerName || "Unknown Customer",
            phone: b.customerPhone || "",
            image: b.customerImage || "",
            count: 0,
            totalSpent: 0,
            lastBookingAt: null,
          };
        }
        repeatMap[key].count += 1;
        repeatMap[key].totalSpent += Number(b.price || 0);
        const when = new Date(b.dateTime || b.createdAt || Date.now());
        if (
          !repeatMap[key].lastBookingAt ||
          when > repeatMap[key].lastBookingAt
        ) {
          repeatMap[key].lastBookingAt = when;
        }
        if (!customerToBookings[key]) customerToBookings[key] = [];
        customerToBookings[key].push(b);
      }
      const repeats = Object.values(repeatMap)
        .filter((r) => r.count > 1)
        .sort((a, b) => b.count - a.count || b.totalSpent - a.totalSpent)
        .slice(0, 10);
      setRepeatCustomers(repeats);
      setRepeatCustomerBookings(customerToBookings);
    } catch (error) {
      console.error("Error fetching vendor bookings:", error);
      console.log("Falling back to mock booking data");
      setBookings(generateMockBookings(15));
    } finally {
      setBookingsLoading(false);
    }
  };

  const generateMockBookings = (count) => {
    const statuses = ["completed", "in-progress", "pending", "cancelled"];
    const services = [
      "Website Development",
      "IT Support",
      "Cybersecurity Audit",
      "Consulting Session",
    ];
    const customers = [
      "John Doe",
      "Jane Smith",
      "Robert Johnson",
      "Sarah Williams",
      "Michael Brown",
    ];
    const phones = [
      "+1 202-555-0147",
      "+1 415-555-0129",
      "+44 20 7946 0958",
      "+91 98765 43210",
      "+61 2 9374 4000",
    ];
    const addresses = [
      "123 Main St, Springfield",
      "45 Market Ave, San Francisco",
      "10 Downing St, London",
      "221B Baker St, London",
      "1600 Amphitheatre Pkwy, Mountain View",
    ];
    const descriptions = [
      "Regular customer interested in web services.",
      "Prefers weekend appointments.",
      "Requested follow-up on last project.",
      "VIP customer with priority support.",
      "Budget-conscious; confirm quote in advance.",
    ];

    return Array.from({ length: count }, (_, i) => {
      const randomStatus =
        statuses[Math.floor(Math.random() * statuses.length)];
      const randomService =
        services[Math.floor(Math.random() * services.length)];
      const randomCustomer =
        customers[Math.floor(Math.random() * customers.length)];
      const randomDate = new Date(
        Date.now() - Math.floor(Math.random() * 90) * 24 * 60 * 60 * 1000,
      );
      const randomPrice = [2500, 150, 800, 200][Math.floor(Math.random() * 4)];
      const randomRating =
        randomStatus === "completed"
          ? (Math.random() * 2 + 3).toFixed(1)
          : null;

      return {
        id: `B${1000 + i}`,
        customerName: randomCustomer,
        serviceName: randomService,
        dateTime: randomDate,
        status: randomStatus,
        price: randomPrice,
        rating: randomRating,
        customerPhone: phones[i % phones.length],
        customerAddress: addresses[i % addresses.length],
        customerDescription: descriptions[i % descriptions.length],
      };
    });
  };

  const getFilteredEarnings = () => {
    if (!analytics) return 0;

    let result;
    switch (earningsFilter) {
      case "week":
        result = analytics.thisWeekEarnings || 0;
        break;
      case "month":
        result = analytics.thisMonthEarnings || 0;
        break;
      case "year":
        result = analytics.thisYearEarnings || 0;
        break;
      case "allTime":
        result = analytics.totalEarnings || 0;
        break;
      default:
        result = 0;
    }

    // Apply platform fee deduction to get net earnings
    const platformFeeRate = taxRates.platformFee || 0;
    const netEarnings = result
      ? Math.max(0, result - result * platformFeeRate)
      : 0;

    console.log(
      `🔍 Earnings filter: ${earningsFilter}, Gross: ${result}, Net: ${netEarnings}`,
      analytics,
    );
    return netEarnings;
  };

  const getFilteredBookings = () => {
    if (bookingsFilter === "all") return bookings;

    return bookings.filter((booking) => booking.status === bookingsFilter);
  };

  const getWeeklyEarningsData = () => {
    if (weeklyEarnings && weeklyEarnings.length > 0) {
      return weeklyEarnings;
    }

    const today = new Date();
    const fallbackData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
      const dayNumber = date.getDate();

      fallbackData.push({
        day: `${dayName} ${dayNumber}`,
        fullDate: date.toISOString().split("T")[0],
        earnings: 0,
        bookings: 0,
        hasEarnings: false,
      });
    }
    return fallbackData;
  };

  const getAllMonthsEarnings = () => {
    if (monthlyEarnings && monthlyEarnings.length > 0) {
      return monthlyEarnings;
    }

    const currentYear = new Date().getFullYear();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return monthNames.map((month) => ({
      month: `${month} ${currentYear}`,
      earnings: 0,
    }));
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: "success",
      "in-progress": "warning",
      pending: "info",
      cancelled: "danger",
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const openBookingModal = (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedBooking(null);
  };

  const acceptBooking = (bookingId) => {
    setBookings((prev) =>
      prev.map((b) =>
        b.id === bookingId && b.status === "pending"
          ? { ...b, status: "in-progress" }
          : b,
      ),
    );
  };

  const handleDeleteService = async (service) => {
    try {
      const serviceId = service.id;
      const params = new URLSearchParams();
      if (service.name) params.set("name", service.name);
      if (service.category) params.set("category", service.category);

      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let success = false;
      let lastErr = null;

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      // Try to delete service from each base URL until one works
      for (const base of bases) {
        try {
          const resp = await fetch(
            `${base}/api/vendors/${id}/services/${encodeURIComponent(
              serviceId,
            )}?${params.toString()}`,
            {
              method: "DELETE",
              headers,
            },
          );

          if (!resp.ok) {
            const body = await resp.text();
            throw new Error(
              `Delete failed: ${resp.status} ${resp.statusText} - ${body}`,
            );
          }

          success = true;
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!success)
        throw (
          lastErr || new Error("Failed to delete service from any endpoint")
        );
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
    } catch (err) {
      console.error("Delete service error:", err);
      setError(err.message || "Failed to delete service");
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button onClick={fetchVendorData} variant="outline-danger">
          Retry
        </Button>
      </Alert>
    );
  }

  if (!vendorData) {
    return <div>No vendor data available</div>;
  }

  console.log("📊 Component re-rendering with analytics:", analytics);
  console.log("📊 Earnings filter:", earningsFilter);

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">
            <FaStore className="me-2 text-primary" />
            {vendorData.businessName}
          </h1>
          <p className="text-muted mb-0">
            {vendorData.businessCategory} • Member since{" "}
            {new Date(vendorData.memberSince).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="overview" title="Overview">
          <Row>
            <Col md={8}>
              {/* Profile Information */}
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaUser className="me-2" />
                    Business Profile
                  </h5>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      fetchAnalytics();
                      fetchTaxRates();
                    }}
                    disabled={analyticsLoading}
                    title="Refresh analytics data"
                  >
                    {analyticsLoading ? <Spinner size="sm" /> : "🔄"}
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <h6>Business Information</h6>
                      <p>
                        <strong>Business Name:</strong>{" "}
                        {vendorData.businessName}
                      </p>
                      <p>
                        <strong>Category:</strong> {vendorData.businessCategory}
                      </p>
                      <p>
                        <strong>Description:</strong>{" "}
                        {vendorData.businessDescription}
                      </p>
                      <p>
                        <strong>Status:</strong>{" "}
                        {getStatusBadge(vendorData.status)}
                      </p>

                      <h6 className="mt-3">Referral Information</h6>
                      <p>
                        <strong>Referral Code:</strong>{" "}
                        {vendorData.referralCode || "—"}
                      </p>
                      <p>
                        <strong>Referral Points:</strong>{" "}
                        {vendorData.referralPoints || 0}
                      </p>
                      <p>
                        <strong>People Referred:</strong>{" "}
                        {vendorData.referralsCount || 0}
                      </p>
                    </Col>
                    <Col md={6}>
                      <h6>Address</h6>
                      <p>
                        <FaMapMarkerAlt className="me-2" />
                        {vendorData.contactInfo?.address || "—"}
                      </p>
                      <p>
                        <strong>Pincode:</strong>{" "}
                        {vendorData.contactInfo?.pincode || "—"}
                      </p>
                      <p>
                        <strong>Society Name:</strong>{" "}
                        {vendorData.contactInfo?.societyName || "—"}
                      </p>

                      <h6 className="mt-3">Preferences</h6>
                      <p>
                        <FaBell className="me-2" />
                        Notifications:{" "}
                        {vendorData.preferences?.notifications ? "On" : "Off"}
                      </p>
                      <p>
                        <FaEnvelope className="me-2" />
                        Email Updates:{" "}
                        {vendorData.preferences?.emailUpdates ? "On" : "Off"}
                      </p>
                      <p>
                        <FaPhone className="me-2" />
                        SMS Updates:{" "}
                        {vendorData.preferences?.smsUpdates ? "On" : "Off"}
                      </p>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col md={12}>
                      <h6>Business Certificate</h6>
                      {vendorData.certificatesUrl ? (
                        <Button
                          variant="outline-primary"
                          href={vendorData.certificatesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Business Certificate (PDF)
                        </Button>
                      ) : (
                        <span className="text-muted">
                          No business certificate uploaded
                        </span>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col md={12}>
                      <h6>Availability</h6>
                      {vendorData.availability &&
                      vendorData.availability.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {vendorData.availability.map((day, index) => (
                            <Badge key={index} bg="info">
                              {day}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">
                          No availability information provided
                        </span>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col md={12}>
                      <h6>Categories and Services</h6>
                      {services && services.length > 0 ? (
                        <div className="row">
                          {services.map((service, index) => (
                            <div key={index} className="col-md-6 mb-3">
                              <div className="border rounded p-3">
                                <div className="d-flex justify-content-between">
                                  <h6 className="mb-1">{service.name}</h6>
                                  <strong>₹{service.price}</strong>
                                </div>
                                <p className="text-muted small mb-2">
                                  {service.description}
                                </p>
                                <Badge bg="secondary">{service.category}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No services listed</span>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Recent Bookings */}
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaCalendar className="me-2" />
                    Recent Bookings
                  </h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setActiveTab("bookings")}
                  >
                    View All
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Service</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Rating</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 5).map((booking) => (
                        <tr key={booking.id}>
                          <td>{booking.customerName}</td>
                          <td>{booking.serviceName}</td>
                          <td>
                            {new Date(booking.dateTime).toLocaleDateString()}
                          </td>
                          <td>{getStatusBadge(booking.status)}</td>
                          <td>₹{booking.price}</td>
                          <td>
                            {booking.rating ? (
                              <div>
                                <FaStar className="text-warning" />
                                <span className="ms-1">{booking.rating}</span>
                              </div>
                            ) : (
                              <span className="text-muted">Pending</span>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openBookingModal(booking)}
                            >
                              <FaEye className="me-1" /> View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              {/* Quick Actions */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Quick Actions</h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-success"
                      onClick={() => navigate(`/vendor-profile/${id}`)}
                    >
                      <FaEye className="me-2" />
                      View Profile
                    </Button>
                    <Button
                      variant="outline-info"
                      onClick={() => setActiveTab("analytics")}
                    >
                      <FaChartLine className="me-2" />
                      View Analytics
                    </Button>
                    <Button
                      variant="outline-warning"
                      onClick={() => setActiveTab("reviews")}
                    >
                      <FaStar className="me-2" />
                      View Reviews
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Subscription Details */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Subscription</h5>
                </Card.Header>
                <Card.Body>
                  {vendorData.subscription ? (
                    <>
                      <div className="d-flex align-items-center mb-2">
                        <Badge
                          bg={
                            (
                              vendorData.subscription.status || ""
                            ).toLowerCase() === "active"
                              ? "success"
                              : (
                                    vendorData.subscription.status || ""
                                  ).toLowerCase() === "expired"
                                ? "secondary"
                                : (
                                      vendorData.subscription.status || ""
                                    ).toLowerCase() === "cancelled"
                                  ? "danger"
                                  : (
                                        vendorData.subscription.status || ""
                                      ).toLowerCase() === "pending"
                                    ? "warning"
                                    : "light"
                          }
                        >
                          {(
                            vendorData.subscription.status || "unknown"
                          ).toUpperCase()}
                        </Badge>
                      </div>
                      <p className="mb-1">
                        <strong>Plan:</strong>{" "}
                        {vendorData.subscription.plan || "—"}
                      </p>
                      <p className="mb-1">
                        <strong>Billing:</strong>{" "}
                        {vendorData.subscription.type || "—"}
                      </p>
                      <p className="mb-1">
                        <strong>Start:</strong>{" "}
                        {vendorData.subscription.startDate
                          ? new Date(
                              vendorData.subscription.startDate,
                            ).toLocaleDateString()
                          : "—"}
                      </p>
                      <p className="mb-0">
                        <strong>End:</strong>{" "}
                        {vendorData.subscription.endDate
                          ? new Date(
                              vendorData.subscription.endDate,
                            ).toLocaleDateString()
                          : "—"}
                      </p>
                    </>
                  ) : (
                    <Alert variant="light" className="mb-0">
                      No subscription found for this vendor.
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Performance Metrics */}
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Performance</h5>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchAnalytics}
                    disabled={analyticsLoading}
                    title="Refresh performance data"
                  >
                    {analyticsLoading ? <Spinner size="sm" /> : "🔄"}
                  </Button>
                </Card.Header>
                <Card.Body>
                  {analyticsLoading ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" />
                      <div className="small text-muted mt-1">
                        Loading performance data...
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Response Rate</span>
                          <span className="fw-bold">
                            {analytics
                              ? `${analytics.responseRate.toFixed(1)}%`
                              : "0%"}
                          </span>
                        </div>
                        <ProgressBar
                          now={analytics ? analytics.responseRate : 0}
                          className="mt-1"
                          variant={
                            analytics && analytics.responseRate >= 90
                              ? "success"
                              : analytics && analytics.responseRate >= 70
                                ? "warning"
                                : "danger"
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Completion Rate</span>
                          <span className="fw-bold">
                            {analytics && analytics.totalBookings > 0
                              ? `${Math.round(
                                  (analytics.completedBookings /
                                    analytics.totalBookings) *
                                    100,
                                )}%`
                              : "0%"}
                          </span>
                        </div>
                        <ProgressBar
                          now={
                            analytics && analytics.totalBookings > 0
                              ? Math.round(
                                  (analytics.completedBookings /
                                    analytics.totalBookings) *
                                    100,
                                )
                              : 0
                          }
                          className="mt-1"
                          variant={
                            analytics &&
                            analytics.totalBookings > 0 &&
                            analytics.completedBookings /
                              analytics.totalBookings >=
                              0.9
                              ? "success"
                              : analytics &&
                                  analytics.totalBookings > 0 &&
                                  analytics.completedBookings /
                                    analytics.totalBookings >=
                                    0.7
                                ? "warning"
                                : "danger"
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Customer Satisfaction</span>
                          <span className="fw-bold">
                            {analytics
                              ? `${analytics.averageRating.toFixed(1)}/5`
                              : "0.0/5"}
                          </span>
                        </div>
                        <ProgressBar
                          now={
                            analytics ? (analytics.averageRating / 5) * 100 : 0
                          }
                          className="mt-1"
                          variant={
                            analytics && analytics.averageRating >= 4.5
                              ? "success"
                              : analytics && analytics.averageRating >= 3.5
                                ? "warning"
                                : "danger"
                          }
                        />
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* Monthly Earnings Chart */}
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Monthly Earnings</h5>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchAnalytics}
                    disabled={analyticsLoading}
                  >
                    {analyticsLoading ? <Spinner size="sm" /> : "🔄"}
                  </Button>
                </Card.Header>
                <Card.Body>
                  {analyticsLoading ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" />
                      <div className="small text-muted mt-1">
                        Loading real-time data...
                      </div>
                    </div>
                  ) : (
                    <>
                      {getAllMonthsEarnings()
                        .slice(-12)
                        .map((item, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between align-items-center mb-2"
                          >
                            <span>{item.month}</span>
                            <span
                              className={`fw-bold ${
                                item.earnings > 0
                                  ? "text-success"
                                  : "text-muted"
                              }`}
                            >
                              ₹
                              {item.earnings
                                ? Math.max(
                                    0,
                                    item.earnings -
                                      item.earnings *
                                        (taxRates.platformFee || 0),
                                  ).toLocaleString()
                                : "0"}
                            </span>
                          </div>
                        ))}
                      <div className="mt-3 pt-2 border-top">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold">
                            Total (Last 12 months):
                          </span>
                          <span className="fw-bold text-success">
                            ₹
                            {getAllMonthsEarnings()
                              .slice(-12)
                              .reduce(
                                (sum, month) =>
                                  sum +
                                  (month.earnings
                                    ? Math.max(
                                        0,
                                        month.earnings -
                                          month.earnings *
                                            (taxRates.platformFee || 0),
                                      )
                                    : 0),
                                0,
                              )
                              .toLocaleString()}
                          </span>
                        </div>
                        <small className="text-muted">
                          💡 Data fetched from real completed bookings (platform
                          fee deducted)
                        </small>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="subscription" title="Subscription">
          <Row>
            <Col md={8}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Subscription Details</h5>
                </Card.Header>
                <Card.Body>
                  {vendorData.subscription ? (
                    <>
                      <div className="d-flex align-items-center mb-3">
                        <Badge
                          bg={
                            (
                              vendorData.subscription.status || ""
                            ).toLowerCase() === "active"
                              ? "success"
                              : (
                                    vendorData.subscription.status || ""
                                  ).toLowerCase() === "expired"
                                ? "secondary"
                                : (
                                      vendorData.subscription.status || ""
                                    ).toLowerCase() === "cancelled"
                                  ? "danger"
                                  : (
                                        vendorData.subscription.status || ""
                                      ).toLowerCase() === "pending"
                                    ? "warning"
                                    : "light"
                          }
                        >
                          {(
                            vendorData.subscription.status || "unknown"
                          ).toUpperCase()}
                        </Badge>
                      </div>
                      <Row>
                        <Col md={6}>
                          <p className="mb-2">
                            <strong>Plan:</strong>{" "}
                            {vendorData.subscription.plan || "—"}
                          </p>
                          <p className="mb-2">
                            <strong>Billing:</strong>{" "}
                            {vendorData.subscription.type || "—"}
                          </p>
                        </Col>
                        <Col md={6}>
                          <p className="mb-2">
                            <strong>Start:</strong>{" "}
                            {vendorData.subscription.startDate
                              ? new Date(
                                  vendorData.subscription.startDate,
                                ).toLocaleDateString()
                              : "—"}
                          </p>
                          <p className="mb-0">
                            <strong>End:</strong>{" "}
                            {vendorData.subscription.endDate
                              ? new Date(
                                  vendorData.subscription.endDate,
                                ).toLocaleDateString()
                              : "—"}
                          </p>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <Alert variant="light" className="mb-0">
                      No subscription found for this vendor.
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="services" title="Services">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Services</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                {services.map((service) => (
                  <Col md={6} lg={4} key={service.id} className="mb-3">
                    <Card>
                      <Card.Body>
                        <h6>{service.name}</h6>
                        <p className="text-muted small">
                          {service.description}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <Badge bg="secondary">{service.category}</Badge>
                          <span className="fw-bold">₹{service.price}</span>
                        </div>

                        <div className="mt-2">
                          <div className="mb-2 small">
                            {Array.isArray(service.availability) &&
                            service.availability.length > 0 ? (
                              <>
                                <strong>Availability:</strong>{" "}
                                {service.availability.join(", ")}
                              </>
                            ) : (
                              <span className="text-muted">
                                No availability set
                              </span>
                            )}
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteService(service)}
                          >
                            Delete
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="bookings" title="Bookings">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">All Bookings</h5>
              <Form.Select
                style={{ width: "auto" }}
                value={bookingsFilter}
                onChange={(e) => setBookingsFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Card.Header>
            <Card.Body>
              {bookingsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading bookings...</span>
                  </Spinner>
                  <p className="mt-2 text-muted">Loading bookings...</p>
                </div>
              ) : (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Customer</th>
                      <th>Service</th>
                      <th>Date & Time</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredBookings().map((booking) => (
                      <tr key={booking.id}>
                        <td>{booking.id}</td>
                        <td>{booking.customerName}</td>
                        <td>{booking.serviceName}</td>
                        <td>{new Date(booking.dateTime).toLocaleString()}</td>
                        <td>{getStatusBadge(booking.status)}</td>
                        <td>₹{booking.price}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => openBookingModal(booking)}
                          >
                            <FaEye className="me-1" /> View
                          </Button>
                          {booking.status === "pending" && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="me-1"
                              onClick={() => acceptBooking(booking.id)}
                            >
                              Accept
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="reviews" title="Reviews">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Customer Reviews</h5>
            </Card.Header>
            <Card.Body>
              {reviewsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading reviews...</span>
                  </Spinner>
                  <p className="mt-2">Loading customer reviews...</p>
                </div>
              ) : vendorReviews && vendorReviews.length > 0 ? (
                <ListGroup>
                  {vendorReviews.map((rev) => (
                    <ListGroup.Item key={rev.bookingId || rev.id}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">
                            {rev.customerName ||
                              rev.reviewerName ||
                              rev.userName ||
                              "Anonymous Customer"}
                          </div>
                          {rev.serviceName && (
                            <div className="text-muted small">
                              Service: {rev.serviceName}
                            </div>
                          )}
                          <div className="text-muted small">
                            {rev.createdAt
                              ? new Date(rev.createdAt).toLocaleDateString()
                              : rev.date
                                ? new Date(rev.date).toLocaleDateString()
                                : "Date not available"}
                          </div>
                          <div className="mt-2">
                            {rev.feedback ||
                              rev.comment ||
                              "No comment provided"}
                          </div>
                        </div>
                        <div className="ms-3 text-nowrap">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={
                                i < (rev.rating || 0)
                                  ? "text-warning"
                                  : "text-muted"
                              }
                            />
                          ))}
                          <div className="text-center mt-1">
                            <strong>{rev.rating || 0}</strong>
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <Alert variant="light" className="mb-0">
                  No reviews available.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="analytics" title="Analytics">
          {analyticsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading analytics...</span>
              </Spinner>
              <p className="mt-2">Loading real-time analytics...</p>
            </div>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Earnings Overview</h5>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            fetchAnalytics();
                            fetchTaxRates();
                          }}
                          disabled={analyticsLoading}
                        >
                          {analyticsLoading ? (
                            <Spinner size="sm" />
                          ) : (
                            "🔄 Refresh"
                          )}
                        </Button>
                        <Form.Select
                          style={{ width: "auto" }}
                          value={earningsFilter}
                          onChange={(e) => setEarningsFilter(e.target.value)}
                        >
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                          <option value="year">This Year</option>
                          <option value="allTime">All Time</option>
                        </Form.Select>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="text-center">
                        <h2 className="text-success">
                          ₹{getFilteredEarnings().toLocaleString()}
                        </h2>
                        <p className="text-muted">
                          {earningsFilter === "week" && "This Week Earnings"}
                          {earningsFilter === "month" && "This Month Earnings"}
                          {earningsFilter === "year" && "This Year Earnings"}
                          {earningsFilter === "allTime" &&
                            "Total Lifetime Earnings"}
                        </p>
                      </div>
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-2">
                          <span>Average per Booking (Net)</span>
                          <span className="fw-bold">
                            {analytics && analytics.totalBookings > 0
                              ? `₹${
                                  Math.round(
                                    ((analytics.totalEarnings
                                      ? Math.max(
                                          0,
                                          analytics.totalEarnings -
                                            analytics.totalEarnings *
                                              (taxRates.platformFee || 0),
                                        )
                                      : 0) /
                                      analytics.totalBookings) *
                                      100,
                                  ) / 100
                                }`
                              : "₹0"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Bookings This Period</span>
                          <span className="fw-bold">
                            {analytics && (
                              <>
                                {earningsFilter === "week" &&
                                  (analytics.thisWeekBookings || 0)}
                                {earningsFilter === "month" &&
                                  (analytics.thisMonthBookings || 0)}
                                {earningsFilter === "year" &&
                                  (analytics.thisYearBookings || 0)}
                                {earningsFilter === "allTime" &&
                                  (analytics.totalBookings || 0)}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Customer Satisfaction</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="text-center">
                        <h2 className="text-warning">
                          {analytics
                            ? analytics.averageRating.toFixed(1)
                            : "0.0"}
                          /5
                        </h2>
                        <p className="text-muted">Average Rating</p>
                        <div className="mt-2">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={
                                i < Math.floor(analytics?.averageRating || 0)
                                  ? "text-warning"
                                  : "text-muted"
                              }
                            />
                          ))}
                        </div>
                      </div>
                      <div className="mt-3">
                        <p>
                          <strong>Total Reviews:</strong>{" "}
                          {analytics?.totalBookings || 0}
                        </p>
                        <p>
                          <strong>Response Rate:</strong>{" "}
                          {analytics
                            ? `${analytics.responseRate.toFixed(1)}%`
                            : "0%"}
                        </p>
                        <p>
                          <strong>Completion Rate:</strong>
                          {analytics && analytics.totalBookings > 0
                            ? `${Math.round(
                                (analytics.completedBookings /
                                  analytics.totalBookings) *
                                  100,
                              )}%`
                            : "0%"}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        Weekly Earnings Trend (Last 7 Days)
                      </h5>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            fetchAnalytics();
                            fetchTaxRates();
                          }}
                          disabled={analyticsLoading}
                        >
                          {analyticsLoading ? <Spinner size="sm" /> : "🔄"}
                        </Button>
                        {repeatCustomers.length > 0 && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => setShowRepeatModal(true)}
                          >
                            View Repeat Customers ({repeatCustomers.length})
                          </Button>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <div className="text-center p-2 bg-light rounded">
                            <div className="h5 text-success mb-1">
                              ₹
                              {getWeeklyEarningsData()
                                .reduce(
                                  (sum, day) =>
                                    sum +
                                    (day.earnings
                                      ? Math.max(
                                          0,
                                          day.earnings -
                                            day.earnings *
                                              (taxRates.platformFee || 0),
                                        )
                                      : 0),
                                  0,
                                )
                                .toLocaleString()}
                            </div>
                            <small className="text-muted">
                              Total This Week
                            </small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center p-2 bg-light rounded">
                            <div className="h5 text-primary mb-1">
                              {
                                getWeeklyEarningsData().filter(
                                  (day) => day.hasEarnings,
                                ).length
                              }
                            </div>
                            <small className="text-muted">Active Days</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center p-2 bg-light rounded">
                            <div className="h5 text-info mb-1">
                              {getWeeklyEarningsData().reduce(
                                (sum, day) => sum + day.bookings,
                                0,
                              )}
                            </div>
                            <small className="text-muted">Total Bookings</small>
                          </div>
                        </div>
                      </div>

                      <div
                        className="weekly-chart-container"
                        style={{
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                          paddingBottom: "10px",
                        }}
                      >
                        <div
                          className="d-flex justify-content-between"
                          style={{ minWidth: "max-content" }}
                        >
                          {getWeeklyEarningsData().map((item, index) => (
                            <div
                              key={index}
                              className="text-center"
                              style={{
                                display: "inline-block",
                                minWidth: "80px",
                                flex: "1",
                              }}
                            >
                              <div className="fw-bold text-success">
                                ₹
                                {item.earnings
                                  ? Math.max(
                                      0,
                                      item.earnings -
                                        item.earnings *
                                          (taxRates.platformFee || 0),
                                    ).toLocaleString()
                                  : "0"}
                              </div>
                              <div className="mt-2 small text-muted">
                                {item.day}
                              </div>
                              <div
                                className="mx-auto mt-2"
                                style={{
                                  height: `${Math.max(
                                    10,
                                    item.earnings > 0
                                      ? Math.max(
                                          20,
                                          (item.earnings
                                            ? Math.max(
                                                0,
                                                item.earnings -
                                                  item.earnings *
                                                    (taxRates.platformFee || 0),
                                              )
                                            : 0) / 50,
                                        )
                                      : 10,
                                  )}px`,
                                  width: "50px",
                                  borderRadius: "4px 4px 0 0",
                                  backgroundColor: item.hasEarnings
                                    ? "#dc3545"
                                    : "#e9ecef",
                                  border: item.hasEarnings
                                    ? "2px solid #dc3545"
                                    : "1px solid #dee2e6",
                                }}
                              ></div>
                              {item.hasEarnings && (
                                <div className="mt-1 small text-danger fw-bold">
                                  {item.bookings} booking
                                  {item.bookings !== 1 ? "s" : ""}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Modal
                show={showRepeatModal}
                onHide={() => setShowRepeatModal(false)}
                centered
                size="xl"
              >
                <Modal.Header closeButton>
                  <Modal.Title>Repeat Customers</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {repeatCustomers.length === 0 ? (
                    <div className="text-muted">No repeat customers yet.</div>
                  ) : (
                    <>
                      <Table responsive className="mb-4">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Phone</th>
                            <th>Times Booked</th>
                            <th>Total Spent</th>
                            <th>Last Booking</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repeatCustomers.map((rc) => (
                            <tr key={rc.key}>
                              <td>{rc.name}</td>
                              <td>{rc.phone || "—"}</td>
                              <td>
                                <Badge bg="secondary">{rc.count}</Badge>
                              </td>
                              <td>₹{rc.totalSpent.toFixed(2)}</td>
                              <td>
                                {rc.lastBookingAt
                                  ? new Date(rc.lastBookingAt).toLocaleString()
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {repeatCustomers.map((rc) => (
                        <Card className="mb-3" key={`detail-${rc.key}`}>
                          <Card.Header className="d-flex justify-content-between align-items-center">
                            <div>
                              <div className="fw-bold">{rc.name}</div>
                              <div className="small text-muted">
                                {rc.phone || "—"} • {rc.count} bookings • ₹
                                {rc.totalSpent.toFixed(2)} total
                              </div>
                            </div>
                            <Badge bg="info">
                              Last:{" "}
                              {rc.lastBookingAt
                                ? new Date(
                                    rc.lastBookingAt,
                                  ).toLocaleDateString()
                                : "—"}
                            </Badge>
                          </Card.Header>
                          <Card.Body>
                            <Table responsive size="sm">
                              <thead>
                                <tr>
                                  <th>Booking ID</th>
                                  <th>Date & Time</th>
                                  <th>Service</th>
                                  <th>Status</th>
                                  <th>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(repeatCustomerBookings[rc.key] || [])
                                  .sort(
                                    (a, b) =>
                                      new Date(b.dateTime || b.createdAt) -
                                      new Date(a.dateTime || a.createdAt),
                                  )
                                  .map((b) => (
                                    <tr key={b.id}>
                                      <td>{b.id}</td>
                                      <td>
                                        {new Date(
                                          b.dateTime || b.createdAt,
                                        ).toLocaleString()}
                                      </td>
                                      <td>{b.serviceName || "Service"}</td>
                                      <td>
                                        {(b.status || "pending").toString()}
                                      </td>
                                      <td>
                                        ₹{Number(b.price || 0).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      ))}
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowRepeatModal(false)}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          )}
        </Tab>

        <Tab eventKey="kyc" title="KYC">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">KYC Information</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchKycData}
                disabled={kycLoading}
              >
                {kycLoading ? <Spinner size="sm" /> : "🔄 Refresh"}
              </Button>
            </Card.Header>
            <Card.Body>
              {kycLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading KYC data...</span>
                  </Spinner>
                  <p className="mt-2">Loading KYC information...</p>
                </div>
              ) : kycData ? (
                <>
                  <Row className="mb-4">
                    <Col md={12}>
                      <h6>KYC Status</h6>
                      <div className="d-flex align-items-center">
                        <Badge
                          bg={
                            kycData.status === "verified"
                              ? "success"
                              : kycData.status === "pending"
                                ? "warning"
                                : kycData.status === "rejected"
                                  ? "danger"
                                  : "secondary"
                          }
                        >
                          {kycData.status
                            ? kycData.status.charAt(0).toUpperCase() +
                              kycData.status.slice(1)
                            : "Not Submitted"}
                        </Badge>
                        {kycData.verifiedAt && (
                          <span className="ms-3 text-muted">
                            Verified on{" "}
                            {new Date(kycData.verifiedAt).toLocaleDateString()}
                          </span>
                        )}
                        {kycData.rejectedAt && (
                          <span className="ms-3 text-muted">
                            Rejected on{" "}
                            {new Date(kycData.rejectedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {kycData.status === "rejected" && kycData.rejectionReason && (
                    <Row className="mb-4">
                      <Col md={12}>
                        <Alert variant="danger">
                          <h6>Rejection Reason</h6>
                          <p>{kycData.rejectionReason}</p>
                        </Alert>
                      </Col>
                    </Row>
                  )}

                  {kycData.verifiedBy && (
                    <Row className="mb-4">
                      <Col md={12}>
                        <p>
                          <strong>Verified By:</strong> {kycData.verifiedBy}
                        </p>
                      </Col>
                    </Row>
                  )}

                  {kycData.bankDetails &&
                    (kycData.bankDetails.accountNumber ||
                      kycData.bankDetails.accountHolderName) && (
                      <Row className="mb-4">
                        <Col md={12}>
                          <h6>Bank Details</h6>
                          <div className="border rounded p-3 bg-light">
                            <Row>
                              <Col md={6}>
                                <p>
                                  <strong>Account Holder:</strong>{" "}
                                  {kycData.bankDetails.accountHolderName || "—"}
                                </p>
                                <p>
                                  <strong>Bank Name:</strong>{" "}
                                  {kycData.bankDetails.bankName || "—"}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p>
                                  <strong>Account Number:</strong>{" "}
                                  {kycData.bankDetails.accountNumber
                                    ? "****" +
                                      kycData.bankDetails.accountNumber.slice(
                                        -4,
                                      )
                                    : "—"}
                                </p>
                                <p>
                                  <strong>IFSC Code:</strong>{" "}
                                  {kycData.bankDetails.ifscCode || "—"}
                                </p>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    )}

                  {kycData.upiDetails && kycData.upiDetails.upiId && (
                    <Row className="mb-4">
                      <Col md={12}>
                        <h6>UPI Details</h6>
                        <div className="border rounded p-3 bg-light">
                          <p>
                            <strong>UPI ID:</strong>{" "}
                            {kycData.upiDetails.upiId || "—"}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  )}

                  {kycData.approvedKycDetails && (
                    <Row className="mb-4">
                      <Col md={12}>
                        <h6>Approved KYC Details</h6>
                        <div className="border rounded p-3 bg-light">
                          <Row>
                            <Col md={6}>
                              <p>
                                <strong>Status:</strong>
                                <Badge
                                  className="ms-2"
                                  bg={
                                    kycData.approvedKycDetails.kycStatus ===
                                    "approved"
                                      ? "success"
                                      : kycData.approvedKycDetails.kycStatus ===
                                          "rejected"
                                        ? "danger"
                                        : "secondary"
                                  }
                                >
                                  {kycData.approvedKycDetails.kycStatus
                                    ? kycData.approvedKycDetails.kycStatus
                                        .charAt(0)
                                        .toUpperCase() +
                                      kycData.approvedKycDetails.kycStatus.slice(
                                        1,
                                      )
                                    : "—"}
                                </Badge>
                              </p>
                              {kycData.approvedKycDetails.verifiedAt && (
                                <p>
                                  <strong>Verified At:</strong>{" "}
                                  {new Date(
                                    kycData.approvedKycDetails.verifiedAt,
                                  ).toLocaleString()}
                                </p>
                              )}
                            </Col>
                            <Col md={6}>
                              {kycData.approvedKycDetails.verifiedBy && (
                                <p>
                                  <strong>Verified By:</strong>{" "}
                                  {kycData.approvedKycDetails.verifiedBy}
                                </p>
                              )}
                            </Col>
                          </Row>

                          {kycData.approvedKycDetails.paymentMethods && (
                            <>
                              {kycData.approvedKycDetails.paymentMethods
                                .bankDetails &&
                                (kycData.approvedKycDetails.paymentMethods
                                  .bankDetails.accountNumber ||
                                  kycData.approvedKycDetails.paymentMethods
                                    .bankDetails.accountHolderName) && (
                                  <div className="mt-3">
                                    <h6>Approved Bank Details</h6>
                                    <div className="border rounded p-2 bg-white">
                                      <Row>
                                        <Col md={6}>
                                          <p className="mb-1">
                                            <strong>Account Holder:</strong>{" "}
                                            {kycData.approvedKycDetails
                                              .paymentMethods.bankDetails
                                              .accountHolderName || "—"}
                                          </p>
                                          <p className="mb-1">
                                            <strong>Bank Name:</strong>{" "}
                                            {kycData.approvedKycDetails
                                              .paymentMethods.bankDetails
                                              .bankName || "—"}
                                          </p>
                                        </Col>
                                        <Col md={6}>
                                          <p className="mb-1">
                                            <strong>Account Number:</strong>{" "}
                                            {kycData.approvedKycDetails
                                              .paymentMethods.bankDetails
                                              .accountNumber
                                              ? "****" +
                                                kycData.approvedKycDetails.paymentMethods.bankDetails.accountNumber.slice(
                                                  -4,
                                                )
                                              : "—"}
                                          </p>
                                          <p className="mb-1">
                                            <strong>IFSC Code:</strong>{" "}
                                            {kycData.approvedKycDetails
                                              .paymentMethods.bankDetails
                                              .ifscCode || "—"}
                                          </p>
                                        </Col>
                                      </Row>
                                    </div>
                                  </div>
                                )}

                              {kycData.approvedKycDetails.paymentMethods
                                .upiDetails &&
                                kycData.approvedKycDetails.paymentMethods
                                  .upiDetails.upiId && (
                                  <div className="mt-3">
                                    <h6>Approved UPI Details</h6>
                                    <div className="border rounded p-2 bg-white">
                                      <p className="mb-1">
                                        <strong>UPI ID:</strong>{" "}
                                        {kycData.approvedKycDetails
                                          .paymentMethods.upiDetails.upiId ||
                                          "—"}
                                      </p>
                                    </div>
                                  </div>
                                )}
                            </>
                          )}

                          {kycData.approvedKycDetails.documents &&
                            kycData.approvedKycDetails.documents
                              .aadhaar_url && (
                              <div className="mt-3">
                                <h6>Approved Documents</h6>
                                <div className="border rounded p-2 bg-white">
                                  <p className="mb-1">
                                    <strong>Aadhaar Card:</strong>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="ms-2"
                                      href={
                                        kycData.approvedKycDetails.documents
                                          .aadhaar_url
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View Document
                                    </Button>
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                <Alert variant="light" className="mb-0">
                  No KYC information available for this vendor.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

<Tab eventKey="security-deposit" title="Security Deposit">
  <Card>
    <Card.Header className="d-flex justify-content-between align-items-center">
      <h5 className="mb-0">Security Deposit</h5>
      <Button variant="outline-secondary" size="sm"
        onClick={fetchSecurityDeposit} disabled={securityDepositLoading}>
        {securityDepositLoading ? <Spinner size="sm" /> : "🔄 Refresh"}
      </Button>
    </Card.Header>
    <Card.Body>
      {securityDepositLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2 text-muted">Loading security deposit info...</p>
        </div>
      ) : (
        <>
          {/* ── Summary bar ── */}
          <Row className="mb-4">
            <Col md={12}>
              <h6>Current Status</h6>
              <div className="border rounded p-3 bg-light">
                <div className="d-flex align-items-center mb-3">
                  <Badge
                    bg={
                      securityDeposit?.paid ? "success"
                      : (securityDeposit?.totalPaid > 0) ? "warning"
                      : "danger"
                    }
                    className="me-2"
                  >
                    {securityDeposit?.paid ? "FULLY PAID"
                      : securityDeposit?.totalPaid > 0 ? "PARTIALLY PAID"
                      : "UNPAID"}
                  </Badge>
                  <span className="text-muted small">
                    ₹{securityDeposit?.totalPaid || 0} paid
                    of ₹{securityDeposit?.totalAmount || 1000}
                    {!securityDeposit?.paid && (
                      <strong className="text-danger ms-2">
                        (₹{securityDeposit?.remainingAmount ?? 1000} remaining)
                      </strong>
                    )}
                  </span>
                </div>

                {/* Progress bar */}
                <ProgressBar
                  now={((securityDeposit?.totalPaid || 0) / 1000) * 100}
                  variant={securityDeposit?.paid ? "success" : "warning"}
                  label={`₹${securityDeposit?.totalPaid || 0} / ₹1000`}
                  className="mb-3"
                  style={{ height: '24px' }}
                />

                {/* Summary fields */}
                {(securityDeposit?.totalPaid > 0) && (
                  <Row>
                    <Col md={3}>
                      <p className="mb-1 text-muted small">Total Paid</p>
                      <p className="fw-bold text-success">
                        ₹{securityDeposit.totalPaid?.toLocaleString()}
                      </p>
                    </Col>
                    <Col md={3}>
                      <p className="mb-1 text-muted small">Remaining</p>
                      <p className="fw-bold text-danger">
                        ₹{securityDeposit.remainingAmount?.toLocaleString() ?? 1000}
                      </p>
                    </Col>
                    <Col md={3}>
                      <p className="mb-1 text-muted small">Instalments</p>
                      <p className="fw-bold">
                        {securityDeposit.payments?.length || 0}
                      </p>
                    </Col>
                    {securityDeposit?.paid && (
                      <Col md={3}>
                        <p className="mb-1 text-muted small">Fully Paid On</p>
                        <p className="fw-bold">
                          {securityDeposit.paidAt
                            ? new Date(securityDeposit.paidAt).toLocaleDateString()
                            : "—"}
                        </p>
                      </Col>
                    )}
                  </Row>
                )}
              </div>
            </Col>
          </Row>

          {/* ── Payment history table ── */}
          {securityDeposit?.payments?.length > 0 && (
            <Row className="mb-4">
              <Col md={12}>
                <h6>Payment History</h6>
                <Table responsive bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Payment ID</th>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {securityDeposit.payments.map((p, i) => (
                      <tr key={i}>
                        <td>{p.paymentIndex || i + 1}</td>
                        <td className="fw-bold text-success">₹{p.amount}</td>
                        <td style={{ textTransform: 'capitalize' }}>
                          {p.modeOfPayment
                            ? p.modeOfPayment.replace(/_/g, ' ')
                            : '—'}
                        </td>
                        <td>
                          <small>{p.paymentId || '—'}</small>
                        </td>
                        <td>
                          <small>{p.orderId || '—'}</small>
                        </td>
                        <td>
                          {p.paidAt
                            ? new Date(p.paidAt).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>
                          <Badge bg={p.markedByAdmin ? "info" : "success"}>
                            {p.markedByAdmin ? "Admin" : "Vendor"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td><strong>Total</strong></td>
                      <td className="fw-bold text-success">
                        ₹{securityDeposit.payments.reduce(
                          (sum, p) => sum + (p.amount || 0), 0
                        )}
                      </td>
                      <td colSpan={5}></td>
                    </tr>
                  </tfoot>
                </Table>
              </Col>
            </Row>
          )}

          {/* ── Admin add payment — only show if not fully paid ── */}
          {!securityDeposit?.paid && (
            <>
              <hr />
              <Row>
                <Col md={12}>
                  <h6 className="mb-3">
                    Admin: Record Payment
                    {securityDeposit?.totalPaid > 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        ₹{securityDeposit.remainingAmount} remaining
                      </Badge>
                    )}
                  </h6>

                  {depositMessage && (
                    <Alert variant={depositMessage.type} dismissible
                      onClose={() => setDepositMessage(null)}>
                      {depositMessage.text}
                    </Alert>
                  )}

                  <Alert variant="warning" className="mb-3">
                    <strong>⚠️ Caution:</strong> Each entry is permanent.
                    You can record partial payments — e.g. ₹700 online + ₹300 cash.
                    The deposit is marked complete when total reaches ₹1000.
                  </Alert>

                  <Row className="g-3">
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>
                          Amount (₹) <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max={securityDeposit?.remainingAmount ?? 1000}
                          placeholder={`Max ₹${securityDeposit?.remainingAmount ?? 1000}`}
                          value={adminAmount}
                          onChange={(e) => setAdminAmount(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                          Max: ₹{securityDeposit?.remainingAmount ?? 1000}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>
                          Mode of payment <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={adminModeOfPayment}
                          onChange={(e) => setAdminModeOfPayment(e.target.value)}
                        >
                          <option value="">— select —</option>
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank transfer</option>
                          <option value="upi">UPI</option>
                          <option value="cheque">Cheque</option>
                          <option value="razorpay">Razorpay</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>
                          Payment ID <span className="text-muted">(optional)</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. pay_ABC123"
                          value={adminPaymentId}
                          onChange={(e) => setAdminPaymentId(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>
                          Order ID <span className="text-muted">(optional)</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. order_XYZ"
                          value={adminOrderId}
                          onChange={(e) => setAdminOrderId(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        variant="success"
                        onClick={handleAdminMarkDeposit}
                        disabled={
                          markingDeposit ||
                          !adminModeOfPayment ||
                          !adminAmount ||
                          Number(adminAmount) <= 0
                        }
                        className="w-100"
                      >
                        {markingDeposit
                          ? <><Spinner size="sm" className="me-2" />Saving...</>
                          : "+ Add Payment"}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          )}

          {securityDeposit?.paid && depositMessage && (
            <Alert variant={depositMessage.type} dismissible
              onClose={() => setDepositMessage(null)}>
              {depositMessage.text}
            </Alert>
          )}
        </>
      )}
    </Card.Body>
  </Card>
</Tab>

      </Tabs>

      <Modal show={showBookingModal} onHide={closeBookingModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking ? (
            <div>
              <Row className="mb-3">
                <Col>
                  <h6 className="mb-1">General</h6>
                  <p className="mb-1">
                    <strong>ID:</strong> {selectedBooking.id}
                  </p>
                  <p className="mb-1">
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedBooking.status)}
                  </p>
                  <p className="mb-1">
                    <strong>Date & Time:</strong>{" "}
                    {new Date(selectedBooking.dateTime).toLocaleString()}
                  </p>
                  <p className="mb-0">
                    <strong>Amount:</strong> ₹{selectedBooking.price}
                  </p>
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <h6 className="mb-1">Customer</h6>
                  <p className="mb-1">
                    <strong>Name:</strong> {selectedBooking.customerName}
                  </p>
                  <p className="mb-1">
                    <strong>Phone:</strong>{" "}
                    {selectedBooking.customerPhone || "—"}
                  </p>
                  <p className="mb-1">
                    <strong>Address:</strong>{" "}
                    {selectedBooking.customerAddress || "—"}
                  </p>
                  <p className="mb-0">
                    <strong>Description:</strong>{" "}
                    {selectedBooking.customerDescription || "—"}
                  </p>
                </Col>
                <Col md={6} className="mb-3">
                  <h6 className="mb-1">Service</h6>
                  <p className="mb-1">
                    <strong>Name:</strong> {selectedBooking.serviceName}
                  </p>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center text-muted">No booking selected</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedBooking && selectedBooking.status === "pending" && (
            <Button
              variant="success"
              onClick={() => {
                acceptBooking(selectedBooking.id);
                closeBookingModal();
              }}
            >
              Accept
            </Button>
          )}
          <Button variant="secondary" onClick={closeBookingModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VendorInfo;
